/**
 * Run a re-send: one request per campaign, one undo step for the whole click,
 * then refresh the lists and the undo button and say what happened.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/providers/ToastProvider';
import { getApiErrorMessage } from '@/lib/utils';
import { invalidatePreparedDrops } from '@/hooks/smswiser-campaign-workspace/invalidate-prepared-drops';
import { smswiserCampaignWorkspaceKeys as keys } from '@/hooks/smswiser-campaign-workspace/keys';

import { groupByCampaign, type ResendPlanRow } from '@/features/resend-killed-drops/planResend';
import {
  EMPTY_RESEND_RESULT,
  mergeResendResults,
  postResendKilled,
  type ResendKilledResult,
} from '@/features/resend-killed-drops/resendKilledApi';
import { resendToast } from '@/features/resend-killed-drops/resendToast';

const itemOf = (row: ResendPlanRow) => ({
  id: row.drop.id,
  ...(row.sendAt ? { executeAfter: row.sendAt.toISOString() } : {}),
});

export function useResendKilled() {
  const qc = useQueryClient();
  const toast = useToast();
  const [running, setRunning] = useState(false);

  const run = useCallback(
    async (rows: ResendPlanRow[]): Promise<ResendKilledResult | null> => {
      const undoGroupId = crypto.randomUUID();
      let result = EMPTY_RESEND_RESULT;
      setRunning(true);
      try {
        for (const g of groupByCampaign(rows)) {
          const part = await postResendKilled(g.countryKey, g.wiserId, { items: g.rows.map(itemOf), undoGroupId });
          result = mergeResendResults(result, part);
          qc.invalidateQueries({ queryKey: keys.preparedDropsPrefix(g.countryKey, g.wiserId) });
          invalidatePreparedDrops(qc, g.countryKey);
        }
        resendToast(toast, result);
        return result;
      } catch (e) {
        if (result.sent > 0) resendToast(toast, result);
        toast.error('Re-send failed', getApiErrorMessage(e, 'Could not re-send the killed drops.'));
        return result.results.length > 0 ? result : null;
      } finally {
        setRunning(false);
      }
    },
    [qc, toast],
  );

  return { run, running };
}
